import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import type { Store } from '@warp-drive/core';
import type FeaturesService from 'tio-common/services/features';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import {
  dynamicEligibilityRequest,
  periodsOfEligibilityRequest,
} from 'tio-data/requests/tas-participant-actions';
import type { DynamicEligibility, PeriodOfEligibility } from 'tio-common/types/tuition-assistance';
import { guidedWizardRouteFor } from 'tio-employee/utils/tas/guided-wizard-route';

export interface TasManageRouteModel {
  application: TasApplicationModel;
  applicationId: string;
  /** Fetched the same way as the legacy show route — feeds ManagePage's
   * inEligible/canSubmitEvidence gate (see manage-page.gts). */
  dynamicEligibility: boolean;
  programEligibility: string[];
}

// Mirrors the legacy show route's include list so the manage screen + the
// `deriveAttention` task model have courses, comments, pcs-cards, assistances, etc.
const INCLUDES = [
  'tas-courses',
  'tas-courses.tas-assets.attachments',
  'tas-program-instance',
  'tas-program-instance.tas-program-template',
  'tas-program-instance.tas-program-template.conditions.public-assets',
  'tas-program-instance.tas-program-template.tas-field-options',
  'tas-program-instance.tas-program-template.company.company-setting',
  'tas-program-instance.employee.person',
  'tas-program-instance.employee.tas-participant',
  'tas-program-instance.tas-assets.attachments',
  'tas-assets.attachments',
  'tas-assistances',
  'tas-assistances.attachments',
  'tas-assistances.tas-assets',
  'tas-assistances.tas-assets.attachments',
  'tags',
  'comments',
  'pcs-cards.pcs-card-holder',
  'pcs-cards.pcs-company',
  'pcs-cards.pcs-authorizations',
  'pcs-cards.pcs-card-events',
  'pcs-cards.pcs-transactions',
].join(',');

/**
 * Base route for the TAS course-management ("manage") screen, used by the single
 * shared `authenticated.tas.applications.manage` route (see manage-link.ts for how
 * an application is routed here). Gated behind the `tasCourseManagement` feature
 * flag — when it's off (production), it redirects to the legacy application show
 * screen instead of rendering.
 */
export default class TasManageRoute extends Route {
  @service declare features: FeaturesService;
  @service declare router: RouterService;
  @service declare store: Store;

  beforeModel(transition: Transition) {
    if (this.features.isEnabled('tasCourseManagement')) {
      return;
    }
    const applicationId = transition.to?.params?.['tas_application_id'];
    if (applicationId) {
      this.router.replaceWith('authenticated.tas.applications.show', applicationId);
    } else {
      this.router.replaceWith('authenticated.tas.applications');
    }
  }

  async model({ tas_application_id }: { tas_application_id: string }) {
    const { content } = (await this.store.request(
      findRecord<TasApplicationModel>('tas-application', tas_application_id, {
        include: INCLUDES,
        reload: true,
      })
    )) as { content: { data: TasApplicationModel } };
    const application = content.data;

    const id = application.tasProgramInstance.employee.tasParticipant.id as string;
    const { code } = application.tasProgramInstance.tasProgramTemplate;

    const [dynamicResponse, periodsResponse] = await Promise.all([
      this.store.request(dynamicEligibilityRequest(id)),
      this.store.request(periodsOfEligibilityRequest(id)),
    ]);
    const dynamicEligibility = (dynamicResponse.content as DynamicEligibility)[code] as boolean;
    const programEligibility = (periodsResponse.content as PeriodOfEligibility)[
      code
    ] as unknown as string[];

    return {
      application,
      applicationId: tas_application_id,
      dynamicEligibility,
      programEligibility,
    };
  }

  /**
   * Mirrors the legacy show route: an unsubmitted draft (DEFINE_COURSES) belongs in
   * the guided wizard, not on a detail screen. Without this, reaching manage for a
   * draft rendered a read-only view of an application with nothing in it yet.
   *
   * `replaceWith`, not `transitionTo`, so Back returns to wherever the employee came
   * from instead of landing on the route that immediately bounces them again.
   */
  afterModel(model: TasManageRouteModel) {
    const route = guidedWizardRouteFor(
      model.application.typeClassification,
      model.application.state
    );
    if (route) {
      this.router.replaceWith(route, model.applicationId);
    }
  }
}
